import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

// The delivery charge used to be a literal number hardcoded in both the
// client's OrderForm and the server's order-creation logic - changing it
// meant a code deploy. This is the real control for it now.
const Settings = () => {
  const [deliveryCharge, setDeliveryCharge] = useState("");
  const [showMango, setShowMango] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/settings`);
        setDeliveryCharge(String(response.data.settings.deliveryCharge));
        setShowMango(response.data.settings.showMango !== false);
      } catch (err) {
        console.error("Error fetching settings:", err);
        setError("Couldn't load current settings.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    const value = Number(deliveryCharge);
    if (Number.isNaN(value) || value < 0) {
      setError("Delivery charge must be a non-negative number.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/settings`,
        { deliveryCharge: value, showMango },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaved(true);
    } catch (err) {
      console.error("Error saving settings:", err);
      setError(err.response?.data?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Settings</h2>

      <form
        onSubmit={handleSubmit}
        className="bg-card border border-line rounded-lg p-6 w-full max-w-lg mx-auto space-y-4"
      >
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}
        {saved && (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
            Saved.
          </p>
        )}

        <div>
          <label className="block font-semibold mb-1 text-ink">Delivery Charge (৳)</label>
          <input
            type="number"
            min="0"
            step="1"
            value={deliveryCharge}
            onChange={(e) => setDeliveryCharge(e.target.value)}
            className="w-full p-2 border border-line rounded focus:ring focus:ring-primary/30"
          />
          <p className="text-xs text-sub mt-1">
            Fallback used for mango-only orders (no restaurant) and any restaurant that
            hasn't set its own delivery charge yet. Each restaurant otherwise sets its own
            under Restaurants.
          </p>
        </div>

        <label className="flex items-center text-ink">
          <input
            type="checkbox"
            checked={showMango}
            onChange={(e) => setShowMango(e.target.checked)}
            className="mr-2"
          />
          Show Mango section on the website
        </label>
        <p className="text-xs text-sub -mt-2">
          Turns off the whole Mango page and its nav links site-wide (e.g. out of season) -
          separate from marking an individual variety unavailable under Mango.
        </p>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
};

export default Settings;
